import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import {
    map,
    catchError,
    switchMap, tap, filter, withLatestFrom, mergeMap
} from 'rxjs/operators';
import { ManagementApiService } from '../management-api.service';
import * as RA from 'ramda-adjunct';
import { AddRevotesDialogComponent } from '@emm/shared/dialogs/add-revotes-dialog/add-revotes-dialog.component';
import { ManagementStoreService } from '@emm/management/services/management-store.service';
import {MasterMenuSystemActions} from '@emm/management/store/mms-revote/master-menu-system.actions';
import {DialogService} from '@emm/shared/services/dialog.service';
import {NotificationService} from '@emm/shared/services/notification.service';

@Injectable({
    providedIn: 'root',
})
export class MasterMenuSystemEffects {
    consumer;

    loadAll$ = createEffect(() =>
        this.actions$.pipe(
            ofType(MasterMenuSystemActions.loadAll),
            switchMap(() =>
                this.api.getAllRevotes().pipe(
                    map((revotes) => MasterMenuSystemActions.loadAllSuccess({revotes})),
                    catchError(({error}) => of(MasterMenuSystemActions.loadAllFailure({error})))
                )
            )
        )
    );

    allCategories$ = createEffect(() =>
        this.actions$.pipe(
            ofType(MasterMenuSystemActions.loadAllCategories),
            mergeMap(({consumer}) =>
                this.api.getAllCategories(consumer).pipe(
                    map((categories) => MasterMenuSystemActions.loadAllCategoriesSuccess({categories})),
                    catchError(({error}) => of(MasterMenuSystemActions.loadAllCategoriesFailure({error})))
                )
            )
        )
    );

    allLocations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(MasterMenuSystemActions.loadAllLocations),
            mergeMap(({consumer}) =>
                this.api.getAllLocationsMMS(consumer).pipe(
                    map((locations) => MasterMenuSystemActions.loadAllLocationsSuccess({locations})),
                    catchError(({error}) => of(MasterMenuSystemActions.loadAllLocationsFailure({error}))),
                )
            ),
        ),
    );

    openAddDialog$ = createEffect(() =>
        this.actions$.pipe(
            ofType(MasterMenuSystemActions.openAddDialog),
            withLatestFrom(this.store.queryMasterMenu.locations$, this.store.queryMasterMenu.categories$),
            filter(
                ([_, locationSetTypes, categories]) =>
                    RA.isNonEmptyArray(locationSetTypes) && RA.isNonEmptyArray(categories),
            ),
            switchMap(([_, locations, categories]) => {
                const dialogRef = this.dialog.open(AddRevotesDialogComponent, {
                    width: '600px',
                    data: {
                        locations: this.store.queryMasterMenu.locations$,
                        categories: this.store.queryMasterMenu.categories$,
                    }
                });

                return dialogRef.afterClosed().pipe(
                    filter((result) => result && result === 'submit'),
                    tap(() => {
                        this.notificationService.success('Created successfully');
                    }),
                    map(() => MasterMenuSystemActions.loadAll())
                );
            })
        )
    );

    delete$ = createEffect(() =>
        this.actions$.pipe(
            ofType(MasterMenuSystemActions.delete),
            switchMap(({revote}) =>
                this.dialogService
                    .openConfirmDialog('Are you sure you want to delete', revote.name)
                    .afterClosed()
                    .pipe(
                        switchMap((confirmDelete) =>
                            confirmDelete
                                ? this.api.deleteRevote(revote.id).pipe(
                                    map(() => MasterMenuSystemActions.deleteSuccess()),
                                    catchError(({error}) => of(MasterMenuSystemActions.deleteFailure({error})))
                                )
                                : of(MasterMenuSystemActions.deleteFailure({error: 'No Clicked'}))
                        )
                    )
            )
        )
    );

    deleteSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(MasterMenuSystemActions.deleteSuccess),
            tap(() => {
                this.notificationService.success('Deleted successfully');
            }),
            switchMap(() => of(MasterMenuSystemActions.loadAll()))
        )
    );


    constructor(
        private actions$: Actions,
        private api: ManagementApiService,
        private store: ManagementStoreService,
        public dialog: MatDialog,
        private dialogService: DialogService,
        private notificationService: NotificationService,
    ) {
    }
}
